import React, { useEffect, useState, useRef, useCallback } from 'react';
import Footer from './components/Footer';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { lightTheme, darkTheme, media } from './theme';
import { useDevice } from './hooks/useDevice';
import MobileMainChartPage from './components/mobile/MobileMainChartPage';
import MobileLandscapeView from './components/mobile/MobileLandscapeView';
import MobileSubmissionModal from './components/mobile/MobileSubmissionModal';
import IntroPage from './components/IntroPage';
import { PriceChart } from './components/PriceChart';
import { MSTRPriceChart } from './components/MSTRChart/MSTRPriceChart_New';
import { ContestBanner, ContestPredictionInput } from './components/Contest';

type FilterType = 'all' | 'pending' | 'success' | 'miss' | 'updated' | 'bet' | 'sentiment';
import TimeFrameSelector from './components/TimeFrameSelector';
import ChartModeToggle, { ChartMode } from './components/ChartModeToggle';
import TreasureHuntControls from './components/TreasureHuntControls';
import { Prediction } from './types/predictions';
import { formatPrediction, extendChartData, filterHistoricalData, getInfluencerName } from './utils/priceUtils';
import { formatDate } from './utils/dateUtils';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import MSTRAdminDashboard from './pages/MSTRAdminDashboard';
import PretextDemo from './pages/PretextDemo';
import MobilePinDemo from './pages/MobilePinDemo';
import WebGLPinDemo from './pages/WebGLPinDemo';
import UserAnalyticsDashboard from './components/UserAnalyticsDashboard';
import UserDashboard from './pages/UserDashboard';
import BitcoinWrapped from './components/BitcoinWrapped';
import { WrappedUpgrade } from './pages/WrappedUpgrade';
import Pricing from './pages/Pricing';
import GenerateWrapped from './pages/GenerateWrapped';
import LeaderboardPage from './pages/LeaderboardPage';
import SubmitPredictionForm from './components/SubmitPredictionForm';
import StablePredictionPanel from './components/PredictionPanel';
import PreAuthModal from './components/PreAuthModal';
import { ScrollProvider } from './context/ScrollContext';
import axios from 'axios';

const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    background: ${props => props.theme.background};
    color: ${props => props.theme.text};
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
      Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  }
  
  @media (max-width: 768px) {
    html, body {
      height: auto;
      min-height: 100vh;
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior: none;
      touch-action: manipulation;
      -webkit-overflow-scrolling: touch;
      width: 100%;
    }
  }
`;

interface TableColumn {
  key: 'expert' | 'prediction' | 'target_time' | 'status';
  label: string;
}

const tableColumns: TableColumn[] = [
  { key: 'expert', label: 'Expert' },
  { key: 'prediction', label: 'Prediction' },
  { key: 'target_time', label: 'Target Time' },
  { key: 'status', label: 'Status' }
];

const Header = styled.header`
  background: ${props => props.theme.surface};
  padding: 16px 24px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.mobile} {
    padding: 8px 16px 8px 12px; /* Reduced from 12px to 8px */
    h1 { font-size: 18px; }
  }
  
  /* PWA mode safe area support */
  @media (display-mode: standalone) {
    padding-top: max(16px, env(safe-area-inset-top) + 8px);
    
    ${media.mobile} {
      padding-top: max(12px, env(safe-area-inset-top) + 8px);
    }
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  ${media.mobile} {
    gap: 12px; /* Reduced gap on mobile */
  }
`;

const HeaderLogo = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  
  ${media.mobile} {
    width: 48px; /* Reduced from 56px to 48px */
    height: 48px; /* Reduced from 56px to 48px */
    border-radius: 8px; /* Reduced from 10px to 8px */
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.primary};
  margin: 0;
  font-size: 24px;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  ${media.mobile} {
    gap: 8px;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: 1px solid ${props => props.theme.primary};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);

  &:hover {
    background: ${props => props.theme.primary};
    opacity: 0.8;
  }

  /* ✅ FIX: Add proper spacing for desktop */
  .submit-text span:not(:last-child)::after {
    content: ' '; /* Add space after "Submit" on desktop */
  }

  ${media.mobile} {
    padding: 10px 12px;
    font-size: 12px;
    flex-direction: column;
    gap: 4px;
    line-height: 1.1;
    min-width: 60px;
    min-height: 44px;
    
    .submit-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      line-height: 1.2;
      gap: 2px;
    }
    
    /* ✅ REMOVE space on mobile since text is stacked */
    .submit-text span:not(:last-child)::after {
      content: none;
    }
    
    .plus-icon {
      font-size: 14px;
      margin-bottom: 2px;
    }
  }
`;

const ThemeToggle = styled.button`
  background: ${props => props.theme.surface};
  color: white;
  border: 2px solid ${props => props.theme.primary};
  padding: 10px 16px;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  font-weight: 600;
  font-size: 16px;
  box-shadow: 0 3px 8px rgba(247, 147, 26, 0.2), 
              0 1px 3px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, ${props => props.theme.primary} 0%, #FFB84D 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover {
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(247, 147, 26, 0.3), 
                0 3px 6px rgba(0, 0, 0, 0.15),
                inset 0 1px 0 rgba(255, 255, 255, 0.2);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(247, 147, 26, 0.25), 
                0 1px 2px rgba(0, 0, 0, 0.1);
  }

  ${media.mobile} {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 20px;
  }
`;

const SignInWithXButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #000000, #14171a);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 16px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #14171a, #1a1a1a);
    border-color: rgba(247, 147, 26, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  ${media.mobile} {
    padding: 8px 12px;
    font-size: 12px;
    
    .btn-text {
      display: none;
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const UserProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(247, 147, 26, 0.1);
  border: 1px solid rgba(247, 147, 26, 0.3);
  padding: 6px 12px;
  border-radius: 24px;
  
  ${media.mobile} {
    padding: 4px 6px;
    gap: 4px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const UserHandle = styled.span`
  color: #f7931a;
  font-size: 14px;
  font-weight: 600;
  
  ${media.mobile} {
    display: none;
  }
`;

const SignOutButton = styled.button`
  background: transparent;
  color: #888;
  border: none;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px;
  
  &:hover {
    color: #ff4444;
    background: rgba(255, 68, 68, 0.1);
  }
  
  ${media.mobile} {
    padding: 2px 4px;
    font-size: 10px;
  }
`;

const NavLink = styled.a`
  color: #aaa;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    color: #f7931a;
    background: rgba(247, 147, 26, 0.1);
  }
  
  ${media.mobile} {
    display: none;
  }
`;

const WrappedNavButton = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #f7931a, #ff6b00);
  color: #000;
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(247, 147, 26, 0.4);
  }
  
  ${media.mobile} {
    display: none;
  }
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => (props.$isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  padding: 24px;
  max-width: 550px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;

  h2 {
    color: ${props => props.theme.primary};
    margin-top: 0;
    margin-bottom: 20px;
  }

  ${media.mobile} {
    padding: 16px;
    width: 95%;
  }
`;

const Container = styled.div<{ $isScreenshotMode?: boolean }>`
  background: ${props => props.$isScreenshotMode ? props.theme.surface : props.theme.background};
  min-height: 100vh;
  ${props => props.$isScreenshotMode && `
    padding: 0;
    margin: 0;
    overflow: hidden;
    width: 100vw;
    height: 100vh;
  `}
`;

const MainContent = styled.div<{ $isScreenshotMode?: boolean }>`
  display: grid;
  grid-template-columns: minmax(280px, 320px) 1fr minmax(280px, 320px);
  gap: 8px;
  padding: 8px 16px 24px 16px;
  max-width: 2000px;
  margin: 0 auto;
  align-items: stretch;

  ${props => props.$isScreenshotMode && `
    display: block;
    padding: 0;
    margin: 0;
    max-width: none;
    height: 100vh;
    width: 100vw;
  `}

  ${media.mobile} {
    grid-template-columns: 1fr;
    padding: 8px;
    gap: 24px;
  }

  ${media.tablet} {
    grid-template-columns: minmax(260px, 300px) 1fr minmax(260px, 300px);
    padding: 8px 12px 16px 12px;
    gap: 6px;
  }
`;

const ChartArea = styled.div<{ $isScreenshotMode?: boolean }>`
  display: ${props => props.$isScreenshotMode ? 'block' : 'flex'};
  flex-direction: column;
  background: ${props => props.theme.surface};
  border-radius: ${props => props.$isScreenshotMode ? '0' : '12px'};
  box-shadow: ${props => props.$isScreenshotMode ? 'none' : '0 4px 6px rgba(0,0,0,0.1)'};
  border: ${props => props.$isScreenshotMode ? 'none' : `1px solid ${props.theme.border}`};
  height: ${props => props.$isScreenshotMode ? '100vh' : 'calc(100vh - 300px)'};
  min-height: ${props => props.$isScreenshotMode ? '100vh' : '600px'};
  max-height: ${props => props.$isScreenshotMode ? '100vh' : 'calc(100vh - 200px)'};
  overflow: hidden;
  ${props => props.$isScreenshotMode && `
    width: 100vw;
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    margin: 0;
  `}
`;

const MainTable = styled.div`
  grid-column: 1 / -1;
  background: ${props => props.theme.surface};
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 24px;
  color: ${props => props.theme.text};

  h3 {
    color: ${props => props.theme.text};
    margin-bottom: 16px;
  }

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    
    th, td {
      padding: 12px;
      border-bottom: 1px solid ${props => props.theme.border};
      text-align: left;
      color: ${props => props.theme.text};
    }

    th {
      background: ${props => props.theme.tableHeaderBackground};
      font-weight: 600;
      color: ${props => props.theme.text};
    }

    tr {
      &:hover {
        background: ${props => props.theme.hoverBackground};
      }
      
      td {
        color: ${props => props.theme.text};
      }
    }

    tr:last-child td {
      border-bottom: none;
    }

    a {
      color: ${props => props.theme.link};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
        opacity: 0.8;
      }
    }
  }

  ${media.mobile} {
    overflow-x: auto;
    
    table {
      font-size: 14px;
      
      th, td {
        padding: 8px;
        white-space: nowrap;
      }
    }
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.85em;
  background: ${props => {
    switch (props.$status) {
      case 'success': return props.theme.successBackground;
      case 'pending': return props.theme.pendingBackground;
      case 'miss': return props.theme.failedBackground;
      case 'updated': return '#8B5CF6';
      default: return props.theme.defaultBackground;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'success': return props.theme.successText;
      case 'pending': return props.theme.pendingText;
      case 'miss': return props.theme.failedText;
      case 'updated': return '#FFFFFF';
      default: return props.theme.defaultText;
    }
  }};
  border: 1px solid currentColor;
`;

const FullscreenSuggestion = styled.div<{ $isDismissed: boolean }>`
  display: ${props => props.$isDismissed ? 'none' : 'flex'};
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #f7931a;
  border-radius: 12px;
  padding: 12px 20px;
  z-index: 1000;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(247, 147, 26, 0.3);
  max-width: 90vw;
  
  /* Only show on tablet/small desktop (768px - 1200px) */
  @media (max-width: 767px) {
    display: none;
  }
  
  @media (min-width: 1200px) {
    display: none;
  }
`;

const MobileTiltSuggestion = styled.div<{ $isDismissed: boolean }>`
  display: none;
  
  /* Only show on mobile portrait */
  @media (max-width: 767px) and (orientation: portrait) {
    display: ${props => props.$isDismissed ? 'none' : 'flex'};
    position: fixed;
    bottom: 120px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border: 1px solid #f7931a;
    border-radius: 12px;
    padding: 10px 16px;
    z-index: 1000;
    align-items: center;
    gap: 10px;
    box-shadow: 0 4px 20px rgba(247, 147, 26, 0.3);
    max-width: 85vw;
  }
`;

const SuggestionText = styled.span`
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  
  span {
    color: #f7931a;
    font-weight: bold;
  }
`;

const SuggestionDismiss = styled.button`
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  line-height: 1;
  
  &:hover {
    color: #fff;
  }
`;

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Move MainLayout to top-level to prevent remounts on every App render
const MainLayout: React.FC<{
  layout: 'mobile' | 'mobile-landscape' | 'desktop';
  isDarkMode: boolean;
  setIsDarkMode: (v: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  isModalOpen: boolean;
  predictions: Prediction[];
  mstrPredictions: Prediction[];
  chartData: Array<{ time: string; value: number }>;
  mstrChartData: Array<{ time: string; value: number }>;
  btcActivePredictions: number[];
  mstrActivePredictions: number[];
  btcActiveFilter: FilterType;
  mstrActiveFilter: FilterType;
  isLoading: boolean;
  togglePrediction: (predictionId: number, assetType: 'btc' | 'mstr') => void;
  applyFilter: (filter: FilterType, assetType: 'btc' | 'mstr') => void;
  resetChart: (assetType: 'btc' | 'mstr') => void;
  btcTimeFrame: string;
  mstrTimeFrame: string;
  onBtcTimeFrameChange: (timeFrame: string) => void;
  onMstrTimeFrameChange: (timeFrame: string) => void;
  filterDataByTimeFrame: (data: Array<{ time: string; value: number }>, timeFrame: string) => Array<{ time: string; value: number }>;
  previewPrediction: Prediction | null;
  handlePreviewGenerated: (extractionResult: any) => void;
  clearPreview: () => void;
  chartMode: ChartMode;
  onChartModeChange: (mode: ChartMode) => void;
  treasureHuntIndex: number;
  onTreasureHuntNavigate: (index: number) => void;
  isScreenshotMode: boolean;
}> = ({
  layout,
  isDarkMode,
  setIsDarkMode,
  openModal,
  closeModal,
  isModalOpen,
  predictions,
  mstrPredictions,
  chartData,
  mstrChartData,
  btcActivePredictions,
  mstrActivePredictions,
  btcActiveFilter,
  mstrActiveFilter,
  isLoading,
  togglePrediction,
  applyFilter,
  resetChart,
  btcTimeFrame,
  mstrTimeFrame,
  onBtcTimeFrameChange,
  onMstrTimeFrameChange,
  filterDataByTimeFrame,
  previewPrediction,
  handlePreviewGenerated,
  clearPreview,
  chartMode,
  onChartModeChange,
  treasureHuntIndex,
  onTreasureHuntNavigate,
  isScreenshotMode
}) => {
  const location = useLocation();
  const currentAsset = location.pathname.includes('/mstr') ? 'mstr' : 'btc';
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [deepLinkHandled, setDeepLinkHandled] = React.useState(false);
  const [fullscreenDismissed, setFullscreenDismissed] = React.useState(() => {
    return localStorage.getItem('fullscreenSuggestionDismissed') === 'true';
  });
  const [tiltDismissed, setTiltDismissed] = React.useState(() => {
    return localStorage.getItem('tiltSuggestionDismissed') === 'true';
  });
  
  // User auth state for contest banner
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [userHandle, setUserHandle] = React.useState<string | undefined>();
  const [userContestPrediction, setUserContestPrediction] = React.useState<number | null>(null);
  const [userContestEntry, setUserContestEntry] = React.useState<{
    price_prediction: number;
    submitted_at: string;
    id: number;
  } | null>(null);
  const [contestEntryCount, setContestEntryCount] = React.useState<number | undefined>();
  const [contestEntries, setContestEntries] = React.useState<Array<{
    id: number;
    twitter_handle: string;
    display_name?: string;
    profile_image_url?: string;
    price_prediction: number;
    submitted_at: string;
    is_current_user?: boolean;
  }>>([]);
  
  // Fetch all contest entries for display on chart
  const fetchContestEntries = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('jwt_token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/contest/bitcoin2026/entries', { headers });
      const data = await response.json();
      if (data.success && data.entries) {
        setContestEntries(data.entries);
        setContestEntryCount(data.entries.length);
      }
    } catch (err) {
      console.warn('Failed to fetch contest entries:', err);
    }
  }, []);
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('jwt_token');
    setIsLoggedIn(false);
    setUserHandle(undefined);
    setUserContestPrediction(null);
    setUserContestEntry(null);
  };

  // Check auth on mount and fetch contest entry
  React.useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('jwt_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setIsLoggedIn(true);
          setUserHandle(payload.x_handle);
          // Ensure both keys are set
          localStorage.setItem('authToken', token);
          localStorage.setItem('jwt_token', token);
          
          // Fetch user's contest entry
          fetch('/api/contest/bitcoin2026/my-entry', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(r => r.json())
            .then(d => {
              if (d.success && d.entry) {
                setUserContestPrediction(d.entry.price_prediction);
                setUserContestEntry(d.entry);
              }
            })
            .catch(() => {});
        }
      } catch (e) {
        console.error('Invalid token');
      }
    }
    
    // Always fetch contest entries for chart display (works with or without login)
    fetchContestEntries();
  }, [fetchContestEntries]);

  // Define handleTogglePrediction early so it can be used in useEffect
  const handleTogglePrediction = React.useCallback((predictionId: number) => {
    togglePrediction(predictionId, currentAsset);
  }, [togglePrediction, currentAsset]);

  // Listen for close events from tweet cards on the chart
  React.useEffect(() => {
    const handlePredictionClose = (e: Event) => {
      const customEvent = e as CustomEvent<{ predictionId: number }>;
      if (customEvent.detail?.predictionId) {
        handleTogglePrediction(customEvent.detail.predictionId);
      }
    };
    window.addEventListener('prediction-close', handlePredictionClose);
    return () => window.removeEventListener('prediction-close', handlePredictionClose);
  }, [handleTogglePrediction]);

  // Handle deep link URL parameter (?p=123) to auto-activate a prediction
  React.useEffect(() => {
    if (deepLinkHandled) return;
    
    const searchParams = new URLSearchParams(location.search);
    const predictionId = searchParams.get('p');
    
    if (predictionId) {
      const predId = parseInt(predictionId, 10);
      const currentPredictions = currentAsset === 'btc' ? predictions : mstrPredictions;
      const predictionExists = currentPredictions.some(p => p.id === predId);
      
      if (predictionExists && !isLoading) {
        console.log(`🔗 Deep link: Activating prediction ${predId}`);
        // Clear any existing active predictions first
        const currentActive = currentAsset === 'btc' ? btcActivePredictions : mstrActivePredictions;
        currentActive.forEach(id => {
          if (id !== predId) togglePrediction(id, currentAsset);
        });
        // Activate the linked prediction if not already active
        if (!currentActive.includes(predId)) {
          togglePrediction(predId, currentAsset);
        }
        setDeepLinkHandled(true);
      }
    }
  }, [location.search, predictions, mstrPredictions, currentAsset, isLoading, deepLinkHandled, btcActivePredictions, mstrActivePredictions, togglePrediction]);

  // Keyboard navigation for both treasure hunt and overview modes
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere if user is typing in an input field
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          (event.target as HTMLElement)?.contentEditable === 'true') {
        return;
      }

      const currentPredictionsArray = currentAsset === 'btc' ? predictions : mstrPredictions;
      if (currentPredictionsArray.length === 0) return;

      if (chartMode === 'treasure-hunt') {
        // TREASURE HUNT MODE: Navigate through all predictions using index
        switch (event.code) {
          case 'ArrowLeft':
            event.preventDefault();
            if (treasureHuntIndex > 0) {
              onTreasureHuntNavigate(treasureHuntIndex - 1);
            }
            break;
            
          case 'ArrowRight':
            event.preventDefault();
            if (treasureHuntIndex < currentPredictionsArray.length - 1) {
              onTreasureHuntNavigate(treasureHuntIndex + 1);
            }
            break;
            
          case 'Home':
            event.preventDefault();
            onTreasureHuntNavigate(0);
            break;
            
          case 'End':
            event.preventDefault();
            onTreasureHuntNavigate(currentPredictionsArray.length - 1);
            break;
            
          case 'Space':
            event.preventDefault();
            onChartModeChange('overview');
            break;
            
          case 'Escape':
            event.preventDefault();
            onChartModeChange('overview');
            break;
        }
      } else if (chartMode === 'overview') {
        // OVERVIEW MODE: Navigate through FILTERED predictions that match the current UI filter
        const activePredictionsArray = currentAsset === 'btc' ? btcActivePredictions : mstrActivePredictions;
        const currentFilter = currentAsset === 'btc' ? btcActiveFilter : mstrActiveFilter;
        
        // Apply the same filtering logic as PredictionPanel to ensure consistency
        const filteredPredictions = [...currentPredictionsArray]
          .filter(pred => {
            // Apply filter logic (same as PredictionPanel)
            if (currentFilter !== 'all') {
              if (currentFilter === 'pending') {
                if (pred.success_status && pred.success_status !== 'pending') return false;
              } else if (currentFilter === 'bet') {
                if (pred.prediction_type !== 'bet') return false;
              } else if (currentFilter === 'sentiment') {
                if (pred.prediction_type !== 'sentiment') return false;
              } else {
                // For 'success' or 'miss' filters
                if (pred.success_status !== currentFilter) return false;
              }
            }
            return true;
          })
          .sort((a, b) => {
            const aDate = new Date(a.prediction_made || a.created_at || 0).getTime();
            const bDate = new Date(b.prediction_made || b.created_at || 0).getTime();
            return bDate - aDate;
          });
        
        if (filteredPredictions.length === 0) return;
        
        switch (event.code) {
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            // Find current active prediction in filtered list
            const currentActiveIndex = filteredPredictions.findIndex(p => activePredictionsArray.includes(p.id));
            if (currentActiveIndex > 0) {
              // Deactivate current, activate previous in filtered list
              const currentId = filteredPredictions[currentActiveIndex].id;
              const previousId = filteredPredictions[currentActiveIndex - 1].id;
              
              if (activePredictionsArray.includes(currentId)) {
                handleTogglePrediction(currentId);
              }
              if (!activePredictionsArray.includes(previousId)) {
                handleTogglePrediction(previousId);
              }
            } else if (activePredictionsArray.length === 0 && filteredPredictions.length > 0) {
              // No predictions active, activate the first one in filtered list
              handleTogglePrediction(filteredPredictions[0].id);
            }
            break;
            
          case 'ArrowRight':
          case 'ArrowDown':
            event.preventDefault();
            // Find current active prediction in filtered list
            const activeIndex = filteredPredictions.findIndex(p => activePredictionsArray.includes(p.id));
            if (activeIndex >= 0 && activeIndex < filteredPredictions.length - 1) {
              // Deactivate current, activate next in filtered list
              const currentId = filteredPredictions[activeIndex].id;
              const nextId = filteredPredictions[activeIndex + 1].id;
              
              if (activePredictionsArray.includes(currentId)) {
                handleTogglePrediction(currentId);
              }
              if (!activePredictionsArray.includes(nextId)) {
                handleTogglePrediction(nextId);
              }
            } else if (activePredictionsArray.length === 0 && filteredPredictions.length > 0) {
              // No predictions active, activate the first one in filtered list
              handleTogglePrediction(filteredPredictions[0].id);
            }
            break;
            
          case 'Home':
            event.preventDefault();
            // Activate first prediction in filtered list only
            activePredictionsArray.forEach(id => handleTogglePrediction(id)); // Clear all
            if (filteredPredictions.length > 0) {
              handleTogglePrediction(filteredPredictions[0].id);
            }
            break;
            
          case 'End':
            event.preventDefault();
            // Activate last prediction in filtered list only
            activePredictionsArray.forEach(id => handleTogglePrediction(id)); // Clear all
            if (filteredPredictions.length > 0) {
              handleTogglePrediction(filteredPredictions[filteredPredictions.length - 1].id);
            }
            break;
            
          case 'Space':
            event.preventDefault();
            // Toggle between overview and treasure hunt
            onChartModeChange('treasure-hunt');
            break;
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [chartMode, treasureHuntIndex, onTreasureHuntNavigate, onChartModeChange, currentAsset, predictions, mstrPredictions, btcActivePredictions, mstrActivePredictions, btcActiveFilter, mstrActiveFilter, handleTogglePrediction]);

  const currentPredictions = React.useMemo(() => {
    const allPredictions = currentAsset === 'btc' ? predictions : mstrPredictions;
    
    // Add preview prediction if it exists and matches current asset
    if (previewPrediction && 
        ((currentAsset === 'btc' && previewPrediction.asset_type !== 'mstr') || 
         (currentAsset === 'mstr' && previewPrediction.asset_type === 'mstr'))) {
      return [...allPredictions, previewPrediction];
    }
    
    return allPredictions;
  }, [currentAsset, predictions, mstrPredictions, previewPrediction]);

  const currentChartData = React.useMemo(() => {
    const rawData = currentAsset === 'btc' ? chartData : mstrChartData;
    const timeFrame = currentAsset === 'btc' ? btcTimeFrame : mstrTimeFrame;
    return filterDataByTimeFrame(rawData, timeFrame);
  }, [currentAsset, chartData, mstrChartData, btcTimeFrame, mstrTimeFrame, filterDataByTimeFrame]);

  const currentActivePredictions = React.useMemo(() => {
    const baseActivePredictions = currentAsset === 'btc' ? btcActivePredictions : mstrActivePredictions;
    
    // Always include preview prediction ID if it exists and matches current asset
    if (previewPrediction && 
        ((currentAsset === 'btc' && previewPrediction.asset_type !== 'mstr') || 
         (currentAsset === 'mstr' && previewPrediction.asset_type === 'mstr'))) {
      return [...baseActivePredictions, previewPrediction.id];
    }
    
    return baseActivePredictions;
  }, [currentAsset, btcActivePredictions, mstrActivePredictions, previewPrediction]);

  const currentActiveFilter = React.useMemo(() => {
    return currentAsset === 'btc' ? btcActiveFilter : mstrActiveFilter;
  }, [currentAsset, btcActiveFilter, mstrActiveFilter]);

  const handleApplyFilter = React.useCallback((filter: FilterType) => {
    applyFilter(filter, currentAsset);
  }, [applyFilter, currentAsset]);

  const assetInfo = {
    btc: { 
      name: 'proofofprice.com',
      symbol: 'BTC',
      shortName: 'Bitcoin',
      panelTitle: 'Bitcoin Predictions'
    },
    mstr: { 
      name: 'proofofprice.com',
      symbol: 'MSTR',
      shortName: 'Strategy₿',
      panelTitle: 'Strategy₿ Predictions'
    }
  };

  return (
    <Container $isScreenshotMode={isScreenshotMode}>
      {/* Hide header in screenshot mode */}
      {!isScreenshotMode && (
      <Header>
        <HeaderLeft>
          <HeaderLogo>
            <img 
              src="/POP_new_logopng.png" 
              alt="ProofOfPrice Logo" 
              width="64" 
              height="64" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </HeaderLogo>
          <Title>{assetInfo[currentAsset].name}</Title>
        </HeaderLeft>
        <HeaderButtons>
          <NavLink href="/">Home</NavLink>
          <NavLink href="/leaderboard">🏆 Leaderboard</NavLink>
          <NavLink href="/dashboard">Dashboard</NavLink>
          <WrappedNavButton href="/wrapped">
            🎁 <span className="wrapped-text">Wrapped</span>
          </WrappedNavButton>
          {isLoggedIn ? (
            <UserProfileSection>
              <svg viewBox="0 0 24 24" fill="#f7931a" width="16" height="16">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <UserHandle>@{userHandle}</UserHandle>
              <SignOutButton onClick={handleLogout}>Sign out</SignOutButton>
            </UserProfileSection>
          ) : (
            <SignInWithXButton onClick={() => setShowAuthModal(true)}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="btn-text">Sign in</span>
            </SignInWithXButton>
          )}
          <ThemeToggle onClick={() => setIsDarkMode(!isDarkMode)}>
            {isDarkMode ? '◉' : '☾'}
          </ThemeToggle>
        </HeaderButtons>
      </Header>
      )}
      
      {/* TimeFrameSelector and Mode Toggle - Align with chart container - Hide in screenshot mode */}
      {layout === 'desktop' && !isScreenshotMode && (
        <div style={{ 
          maxWidth: '2000px',
          margin: '0 auto',
          padding: '8px 16px 0 16px',
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 320px) 1fr minmax(280px, 320px)',
          gap: '16px',
          alignItems: 'center'
        }}>
          {/* Empty space to align with left column */}
          <div></div>
          
          {/* TimeFrame and Mode controls aligned with chart */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <TimeFrameSelector
              selectedTimeFrame={currentAsset === 'btc' ? btcTimeFrame : mstrTimeFrame}
              onTimeFrameChange={currentAsset === 'btc' ? onBtcTimeFrameChange : onMstrTimeFrameChange}
              isDarkMode={isDarkMode}
              predictions={currentPredictions}
              currentAsset={currentAsset}
            />
            
            <ChartModeToggle
              mode={chartMode}
              onModeChange={onChartModeChange}
              theme={isDarkMode ? darkTheme : lightTheme}
              predictionCount={currentPredictions.length}
            />
          </div>
          
          {/* Empty space to align with right column */}
          <div></div>
        </div>
      )}
      
      <MainContent $isScreenshotMode={isScreenshotMode}>
        {layout === 'mobile' ? (
          <>
            <MobileMainChartPage
              activePredictions={currentActivePredictions}
              togglePrediction={handleTogglePrediction}
              chartData={currentChartData}
              isDarkMode={isDarkMode}
              theme={isDarkMode ? darkTheme : lightTheme}
              predictions={currentPredictions}
              onRefresh={async () => {
                // Refresh chart data and predictions
                await Promise.all([
                  // You can add specific refresh logic here if needed
                  new Promise(resolve => setTimeout(resolve, 1000))
                ]);
              }}
              onResetChart={() => {
                // Reset all active predictions to clear the chart
                resetChart(currentAsset);
              }}
              openModal={openModal}
              isLoggedIn={isLoggedIn}
              userHandle={userHandle}
              userContestPrediction={userContestPrediction}
              userContestEntry={userContestEntry}
              onLogin={() => setShowAuthModal(true)}
              onEntrySubmitted={(price, entry) => {
                setUserContestPrediction(price);
                if (entry) setUserContestEntry(entry);
                // Refresh all contest entries
                fetchContestEntries();
              }}
            />
            
            <MobileSubmissionModal
              isOpen={isModalOpen}
              onClose={closeModal}
              isDarkMode={isDarkMode}
              theme={isDarkMode ? darkTheme : lightTheme}
              asset={currentAsset}
              onSubmissionSuccess={() => {
                closeModal();
                // Refresh predictions after successful submission
                if (currentAsset === 'btc') {
                  // Trigger refresh for BTC predictions
                  window.location.reload();
                } else {
                  // Trigger refresh for MSTR predictions  
                  window.location.reload();
                }
              }}
            />
          </>
        ) : layout === 'mobile-landscape' ? (
          /* Mobile Landscape: Chart with mini prediction panel */
          <MobileLandscapeView
            predictions={currentPredictions}
            chartData={currentChartData}
            isDarkMode={isDarkMode}
            theme={isDarkMode ? darkTheme : lightTheme}
            asset={currentAsset}
            isLoggedIn={isLoggedIn}
            userHandle={userHandle}
            userContestPrediction={userContestPrediction}
            contestEntries={contestEntries}
            onLogin={() => setShowAuthModal(true)}
            onEntrySubmitted={(price, entry) => {
              setUserContestPrediction(price);
              if (entry) setUserContestEntry(entry);
              fetchContestEntries();
            }}
          />
        ) : (
          <>
            {/* Left Column: Submission Form - Hidden in screenshot mode */}
            {!isScreenshotMode && (
            <SubmitPredictionForm 
              isOpen={true}
              onPreviewGenerated={handlePreviewGenerated}
              onClearPreview={clearPreview}
              isDarkMode={isDarkMode}
              theme={isDarkMode ? darkTheme : lightTheme}
            />
            )}
            
            {/* Center: Chart Area - Full width in screenshot mode */}
            <ChartArea $isScreenshotMode={isScreenshotMode}>
                {/* Contest Banner - Only for BTC, hidden in screenshot mode */}
                {currentAsset === 'btc' && !isScreenshotMode && (
                  <ContestBanner
                    theme={isDarkMode ? darkTheme : lightTheme}
                    isLoggedIn={isLoggedIn}
                    userHandle={userHandle}
                    onLogin={() => setShowAuthModal(true)}
                  />
                )}
                <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                {currentAsset === 'btc' ? (
                  <>
                  <PriceChart
                    predictions={currentPredictions}
                    activePredictions={
                      chartMode === 'treasure-hunt' && currentPredictions[treasureHuntIndex]
                        ? [currentPredictions[treasureHuntIndex].id]
                        : currentActivePredictions
                    }
                    chartData={currentChartData}
                    isDarkMode={isDarkMode}
                    theme={isDarkMode ? darkTheme : lightTheme}
                    treasureHuntMode={chartMode === 'treasure-hunt'}
                    currentTreasureHuntPrediction={
                      chartMode === 'treasure-hunt' && currentPredictions[treasureHuntIndex]
                        ? currentPredictions[treasureHuntIndex]
                        : undefined
                    }
                    isScreenshotMode={isScreenshotMode}
                    userContestPrediction={userContestPrediction}
                    userHandle={userHandle}
                    contestEntries={contestEntries}
                  />
                  {/* Contest prediction input overlay */}
                  {!isScreenshotMode && (
                    <ContestPredictionInput
                      theme={isDarkMode ? darkTheme : lightTheme}
                      isLoggedIn={isLoggedIn}
                      userHandle={userHandle}
                      onLogin={() => setShowAuthModal(true)}
                      onEntrySubmitted={(price, entry) => {
                        setUserContestPrediction(price);
                        if (entry) setUserContestEntry(entry);
                        // Refresh all contest entries to show the new one
                        fetchContestEntries();
                      }}
                    />
                  )}
                  </>
                ) : (
                  <MSTRPriceChart
                    predictions={currentPredictions}
                    activePredictions={
                      chartMode === 'treasure-hunt' && currentPredictions[treasureHuntIndex]
                        ? [currentPredictions[treasureHuntIndex].id]
                        : currentActivePredictions
                    }
                    chartData={currentChartData}
                    isDarkMode={isDarkMode}
                    theme={isDarkMode ? darkTheme : lightTheme}
                    treasureHuntMode={chartMode === 'treasure-hunt'}
                    currentTreasureHuntPrediction={
                      chartMode === 'treasure-hunt' && currentPredictions[treasureHuntIndex]
                        ? currentPredictions[treasureHuntIndex]
                        : undefined
                    }
                  />
                )}
                </div>
            </ChartArea>
            
            {/* Right Column: Prediction Panel or Treasure Hunt Controls - Hidden in screenshot mode */}
            {!isScreenshotMode && (
            chartMode === 'overview' ? (
              <StablePredictionPanel
                predictions={currentPredictions}
                activePredictions={currentActivePredictions}
                togglePrediction={handleTogglePrediction}
                activeFilter={currentActiveFilter}
                applyFilter={handleApplyFilter}
                userContestEntry={userContestEntry}
                userHandle={userHandle}
                entryCount={contestEntryCount}
              />
            ) : (
              <TreasureHuntControls
                currentIndex={treasureHuntIndex}
                totalPredictions={currentPredictions.length}
                onNavigate={onTreasureHuntNavigate}
                currentPrediction={currentPredictions[treasureHuntIndex] || null}
                theme={isDarkMode ? darkTheme : lightTheme}
              />
            )
            )}
          </>
        )}
      </MainContent>
      
      {/* Hide table and footer in screenshot mode or mobile-landscape */}
      {!isScreenshotMode && layout !== 'mobile-landscape' && (
        <>
          {/* Desktop only: Predictions History Table (Mobile uses leaderboard inside MobileMainChartPage) */}
          {layout !== 'mobile' && (
            <MainTable>
              <h3>{assetInfo[currentAsset].panelTitle} History</h3>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  Loading {assetInfo[currentAsset].shortName} predictions...
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      {tableColumns.map(column => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentPredictions.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                          No {assetInfo[currentAsset].shortName} predictions yet
                        </td>
                      </tr>
                    ) : (
                      currentPredictions.map((pred) => {
                        return (
                          <tr key={pred.id}>
                            <td>{getInfluencerName(pred)}</td>
                            <td>
                              {formatPrediction(pred)}
                            </td>
                            <td>{formatDate(pred.target_time, 'dd/MM/yyyy')}</td>
                            <td>
                              <StatusBadge $status={pred.success_status || 'pending'}>
                                {pred.success_status || 'pending'}
                              </StatusBadge>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </MainTable>
          )}
      
          {/* Hide footer on mobile Bitcoin pages to make space for bottom nav */}
          {!(layout === 'mobile' && (location.pathname === '/btc' || location.pathname === '/')) && (
            <Footer />
          )}
        </>
      )}
      
      {/* Pre-auth modal */}
      <PreAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        authOptions={{ redirect: 'btc' }}
        usePopup={true}
      />
      
      {/* Fullscreen suggestion for tablet/small desktop */}
      <FullscreenSuggestion $isDismissed={fullscreenDismissed}>
        <SuggestionText>
          <span>Go fullscreen</span> for the best experience
        </SuggestionText>
        <SuggestionDismiss onClick={() => {
          setFullscreenDismissed(true);
          localStorage.setItem('fullscreenSuggestionDismissed', 'true');
        }}>
          ×
        </SuggestionDismiss>
      </FullscreenSuggestion>
      
      {/* Tilt suggestion for mobile portrait */}
      <MobileTiltSuggestion $isDismissed={tiltDismissed}>
        <SuggestionText>
          📱 <span>Tilt your phone</span> for the full chart!
        </SuggestionText>
        <SuggestionDismiss onClick={() => {
          setTiltDismissed(true);
          localStorage.setItem('tiltSuggestionDismissed', 'true');
        }}>
          ×
        </SuggestionDismiss>
      </MobileTiltSuggestion>
    </Container>
  );
};

const App: React.FC = () => {
  const { isMobile, isMobileLandscape } = useDevice();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [mstrPredictions, setMstrPredictions] = useState<Prediction[]>([]);
  
  // SEPARATE active predictions for each asset
  const [btcActivePredictions, setBtcActivePredictions] = useState<number[]>([]);
  const [mstrActivePredictions, setMstrActivePredictions] = useState<number[]>([]);
  
  // SEPARATE filters for each asset  
  const [btcActiveFilter, setBtcActiveFilter] = useState<FilterType>('all');
  const [mstrActiveFilter, setMstrActiveFilter] = useState<FilterType>('all');
  
  // Time frame state for charts - can be set via URL param for screenshots
  const urlTimeFrame = new URLSearchParams(window.location.search).get('tf');
  const [btcTimeFrame, setBtcTimeFrame] = useState<string>(urlTimeFrame || 'ALL');
  const [mstrTimeFrame, setMstrTimeFrame] = useState<string>(urlTimeFrame || 'ALL');
  
  // Preview prediction state for live chart preview
  const [previewPrediction, setPreviewPrediction] = useState<Prediction | null>(null);
  
  // Chart mode state - overview or treasure hunt
  const [chartMode, setChartMode] = useState<ChartMode>('overview');
  
  // Treasure hunt navigation state
  const [treasureHuntIndex, setTreasureHuntIndex] = useState<number>(0);
  
  // Check for screenshot mode URL parameters
  const isScreenshotMode = new URLSearchParams(window.location.search).get('screenshot') === 'true';
  const urlTheme = new URLSearchParams(window.location.search).get('theme');
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // URL param takes precedence for screenshot mode
    if (urlTheme === 'dark') return true;
    if (urlTheme === 'light') return false;
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [chartData, setChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [mstrChartData, setMstrChartData] = useState<Array<{ time: string; value: number }>>([]);
  // Layout: 'mobile' for portrait, 'mobile-landscape' for landscape mobile, 'desktop' for desktop
  const [layout, setLayout] = useState<'mobile' | 'mobile-landscape' | 'desktop'>(
    isMobileLandscape ? 'mobile-landscape' : (isMobile ? 'mobile' : 'desktop')
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Helper function to create formatted prediction text for display
  const createPredictionTextForDisplay = (data: any): string => {
    const formatNumber = (num: number): string => {
      return `$${num.toLocaleString()}`;
    };

    switch (data.prediction_type) {
      case 'target':
        return data.price_target ? formatNumber(data.price_target) : '';
      case 'range':
        if (data.price_min && data.price_max) {
          return `${formatNumber(data.price_min)} - ${formatNumber(data.price_max)}`;
        }
        return '';
      case 'pricetimerange':
        return data.price_min ? `Above ${formatNumber(data.price_min)}` : '';
      case 'top':
        return data.price_max ? `TOP: ${formatNumber(data.price_max)}` : '';
      case 'bottom':
        return data.price_min ? `BOTTOM: ${formatNumber(data.price_min)}` : '';

      case 'sentiment':
        return 'Sentiment prediction';
      default:
        return '';
    }
  };

  // Function to create preview prediction from extraction result
  const createPreviewPrediction = (extractionData: any, assetType: 'btc' | 'mstr' = 'btc'): Prediction => {
    // For sentiment, use today's date as both start and end (shows at current price)
    const isSentiment = extractionData.prediction_type === 'sentiment';
    const today = new Date().toISOString().split('T')[0];
    
    return {
      id: -1, // Special ID for preview
      expert_name: extractionData.expert_name || 'Preview Expert',
      influencer: {
        id: -1,
        name: extractionData.expert_name || 'Preview Expert',
        score: 0, // Default score for preview
        x_handle: extractionData.twitter_handle ? extractionData.twitter_handle.replace('@', '') : 'preview',
        profile_image: extractionData.profile_image_url || undefined
      },
      prediction_type: extractionData.prediction_type || 'target',
      price_target: extractionData.price_target || null,
      price_min: extractionData.price_min || null,
      price_max: extractionData.price_max || null,
      price_prediction: extractionData.price_prediction || createPredictionTextForDisplay(extractionData),
      start_date: extractionData.prediction_start_date || today,
      end_date: isSentiment ? today : (extractionData.target_time || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
      created_at: new Date().toISOString(),
      prediction_made: today, // For sentiment, this is used to find the price
      source_url: extractionData.tweet_url || null,
      // Use source_tweet_text (notes) for quote if tweet_text is not available
      quote: extractionData.tweet_text || extractionData.source_tweet_text || null,
      success_status: 'pending' as const,
      asset_type: assetType,
      notes: extractionData.source_tweet_text || 'PREVIEW - This is a preview of your submission',
      exclude_from_chart: false,
      is_preview: true // Special flag for preview styling
    } as Prediction;
  };

  // Function to handle preview generation
  const handlePreviewGenerated = (extractionResult: any) => {
    if (extractionResult.success && extractionResult.data) {
      const location = window.location;
      const currentAsset = location.pathname.includes('/mstr') ? 'mstr' : 'btc';
      const previewPred = createPreviewPrediction(extractionResult.data, currentAsset);
      setPreviewPrediction(previewPred);
      
    } else {
      // Clear preview if extraction failed
      setPreviewPrediction(null);
    }
  };

  // Function to clear preview
  const clearPreview = () => {
    setPreviewPrediction(null);
  };

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Apply fullscreen styles when in screenshot mode
  useEffect(() => {
    if (isScreenshotMode) {
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      // Cleanup if needed
      if (isScreenshotMode) {
        document.body.style.margin = '';
        document.body.style.padding = '';
        document.body.style.overflow = '';
        document.documentElement.style.margin = '';
        document.documentElement.style.padding = '';
        document.documentElement.style.overflow = '';
      }
    };
  }, [isScreenshotMode]);

  // Fetch BTC data
  useEffect(() => {
    const fetchBTCData = async () => {
      setIsLoading(true);
      try {
        
        const apiBase = '/api';
        const priceUrl = `${apiBase}/prices`;
        const predictionsUrl = `${apiBase}/predictions`;
        
        const [priceResponse, predictionResponse] = await Promise.all([
          axios.get(priceUrl),
          axios.get(predictionsUrl)
        ]);

        if (!priceResponse?.data || !Array.isArray(priceResponse.data) || priceResponse.data.length === 0) {
          throw new Error('No BTC price data available from the API');
        }

        const predictions = predictionResponse?.data && Array.isArray(predictionResponse.data) 
          ? predictionResponse.data 
          : [];
        
        setPredictions(predictions);
        
        const priceData = priceResponse.data;
        const formattedData = priceData.map((price: any) => ({
          time: price.date ? price.date.split('T')[0] : new Date().toISOString().split('T')[0],
          value: Number(price.close || price.value || 0)
        }));
        
        const filteredData = filterHistoricalData(formattedData);
        const extendedData = predictions.length > 0 
          ? extendChartData(filteredData, predictions)
          : filteredData;
        
        if (extendedData.length === 0) {
          throw new Error('No BTC chart data available after processing');
        }
        
        setChartData(extendedData);
        setError(null);
      } catch (error: any) {
        console.error('Failed to fetch BTC data:', error);
        setError(error?.message || 'Failed to fetch BTC data');
        setChartData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBTCData();
  }, []);

  // Fetch MSTR data
  useEffect(() => {
    const fetchMSTRData = async () => {
      try {
        
        const apiBase = '/api';
        const priceUrl = `${apiBase}/mstr/prices`;
        const predictionsUrl = `${apiBase}/mstr/predictions`;
        
        
        // TRY MSTR PRICE API FIRST
        let priceResponse;
        try {
          priceResponse = await axios.get(priceUrl);
          console.log('🔍 MSTR Price API Response Debug:', {
            status: priceResponse.status,
            dataType: typeof priceResponse.data,
            isArray: Array.isArray(priceResponse.data),
            dataLength: priceResponse?.data?.length || 0,
            hasSuccess: priceResponse?.data?.success,
            actualData: priceResponse.data
          });
        } catch (priceError: any) { // ✅ Add type annotation
          console.error('❌ App: MSTR price API failed:', priceError.message);
          console.error('❌ App: Full price error:', priceError);
          throw priceError;
        }

        // TRY MSTR PREDICTIONS API
        let predictionResponse;
        try {
          predictionResponse = await axios.get(predictionsUrl);
          console.log('🔍 MSTR Predictions API Response Debug:', {
            status: predictionResponse.status,
            hasSuccess: predictionResponse?.data?.success,
            dataLength: predictionResponse?.data?.data?.length || 0
          });
        } catch (predError: any) { // ✅ Add type annotation
          console.error('❌ App: MSTR predictions API failed:', predError.message);
          // Don't throw - predictions are optional
          predictionResponse = { data: { success: false, data: [] } };
        }

        // Handle MSTR predictions
        const mstrPredictionsData = predictionResponse?.data?.success && predictionResponse?.data?.data 
          ? predictionResponse.data.data.map((item: any) => ({
              id: item.id,
              influencer_id: item.influencer_id, // FIX: use influencer_id from backend
              price_prediction: item.price_prediction,
              target_time: item.target_time,
              success_status: item.success_status || 'pending',
              quote: item.notes,
              vote_score: 0,
              created_at: item.created_at,
              start_date: item.start_date || item.prediction_made?.split('T')[0] || item.created_at?.split('T')[0],
              end_date: item.end_date || item.target_time,
              price_min: item.price_min,
              price_max: item.price_max,
              price_target: item.target_price,
              exclude_from_chart: false,
              prediction_type: item.prediction_type || 'target',
              influencer: {
                id: item.influencer?.id || item.influencer_id,
                name: item.influencer?.name || 'Unknown Expert',
                score: item.influencer?.score || item.influencer_score || 0
              }
            }))
          : [];
        
        setMstrPredictions(mstrPredictionsData);
        
        // Handle MSTR price data - CHECK RESPONSE STRUCTURE
        
        // CHECK IF IT'S IN {success: true, data: [...]} FORMAT
        let actualPriceData;
        if (priceResponse?.data?.success && priceResponse?.data?.data) {
          actualPriceData = priceResponse.data.data;
        } else if (Array.isArray(priceResponse?.data)) {
          actualPriceData = priceResponse.data;
        } else {
          console.error('❌ App: Unknown MSTR price data structure:', priceResponse?.data);
          setMstrChartData([]);
          return;
        }
        
        if (actualPriceData && Array.isArray(actualPriceData) && actualPriceData.length > 0) {
          
          const formattedData = actualPriceData.map((price: any) => ({
            time: price.date ? price.date.split('T')[0] : new Date().toISOString().split('T')[0],
            value: Number(price.close || price.value || 0)
          }));
          
          
          const filteredData = filterHistoricalData(formattedData);
          
          const extendedData = mstrPredictionsData.length > 0 
            ? extendChartData(filteredData, mstrPredictionsData)
            : filteredData;
          
          setMstrChartData(extendedData);
        } else {
          console.warn('❌ App: No valid MSTR price data found');
          setMstrChartData([]);
        }
        
      } catch (error: any) {
        console.error('❌ App: Failed to fetch MSTR data:', error.message);
        console.error('❌ App: Error details:', {
          name: error.name,
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        setMstrChartData([]);
        setMstrPredictions([]);
      }
    };

    fetchMSTRData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMobileLandscape) {
        setLayout('mobile-landscape');
      } else if (isMobile) {
        setLayout('mobile');
      } else {
        setLayout('desktop');
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isMobile, isMobileLandscape]);

  // REPLACE with stable functions that take asset as parameter
  const togglePrediction = useCallback((predictionId: number, assetType: 'btc' | 'mstr') => {
    
    const currentPredictions = assetType === 'btc' ? predictions : mstrPredictions;
    const currentPrediction = currentPredictions.find(p => p.id === predictionId);
    
    // Check if this is a bet prediction and find its opponent
    let betOpponentId: number | null = null;
    if (currentPrediction?.prediction_type === 'bet') {
      
      // Find bet opponent with same price/date range
      const betOpponent = currentPredictions.find(p => 
        p.id !== predictionId &&
        p.prediction_type === 'bet' &&
        (p.price_prediction || p.price_target || p.price_min) === 
          (currentPrediction.price_prediction || currentPrediction.price_target || currentPrediction.price_min) &&
        p.start_date === currentPrediction.start_date &&
        p.end_date === currentPrediction.end_date
      );
      
      if (betOpponent) {
        betOpponentId = betOpponent.id;
      }
    }
    
    if (assetType === 'btc') {
      setBtcActivePredictions(prev => {
        let newPredictions: number[];
        
        if (prev.includes(predictionId)) {
          // Deactivating - remove both bet participants if it's a bet
          newPredictions = prev.filter(id => 
            id !== predictionId && (betOpponentId ? id !== betOpponentId : true)
          );
        } else {
          // Activating - add both bet participants if it's a bet
          newPredictions = [...prev, predictionId];
          if (betOpponentId && !prev.includes(betOpponentId)) {
            newPredictions.push(betOpponentId);
          }
        }
        
        return newPredictions;
      });
    } else {
      setMstrActivePredictions(prev => {
        let newPredictions: number[];
        
        if (prev.includes(predictionId)) {
          // Deactivating - remove both bet participants if it's a bet
          newPredictions = prev.filter(id => 
            id !== predictionId && (betOpponentId ? id !== betOpponentId : true)
          );
        } else {
          // Activating - add both bet participants if it's a bet
          newPredictions = [...prev, predictionId];
          if (betOpponentId && !prev.includes(betOpponentId)) {
            newPredictions.push(betOpponentId);
          }
        }
        
        return newPredictions;
      });
    }
  }, [predictions, mstrPredictions]);

  const applyFilter = useCallback((filter: FilterType, assetType: 'btc' | 'mstr') => {
    
    const currentPredictions = assetType === 'btc' ? predictions : mstrPredictions;
    const currentActiveFilter = assetType === 'btc' ? btcActiveFilter : mstrActiveFilter;

    // If the filter is already active, deactivate all predictions and reset filter to 'all'
    if (currentActiveFilter === filter) {
      if (assetType === 'btc') {
        setBtcActivePredictions([]);
        setBtcActiveFilter('all');
      } else {
        setMstrActivePredictions([]);
        setMstrActiveFilter('all');
      }
      return;
    }

    // ✅ ADD: Debug Ed Juline specifically
    const edJulinePrediction = currentPredictions.find(p => p.id === 14);
    console.log('🔍 Ed Juline Debug:', {
      found: !!edJulinePrediction,
      filter: filter,
      edJulineStatus: edJulinePrediction?.success_status,
      wouldPassFilter: edJulinePrediction ? (
        filter === 'all' || 
        (filter === 'pending' && (!edJulinePrediction.success_status || edJulinePrediction.success_status === 'pending')) ||
        edJulinePrediction.success_status === filter
      ) : false
    });
    
    if (filter === 'all') {
      // For 'all' filter, include ALL predictions with Easter Bunny at end
      const sortedPredictions = [...currentPredictions].sort((a, b) => {
        // Always put Easter Bunny prediction (ID 331) at the end
        if (a.id === 331) return 1;
        if (b.id === 331) return -1;
        // Keep original order for others (no additional sorting)
        return 0;
      });
      const allIds = sortedPredictions.map(p => p.id);
      if (assetType === 'btc') {
        setBtcActivePredictions(allIds);
        setBtcActiveFilter(filter);
      } else {
        setMstrActivePredictions(allIds);
        setMstrActiveFilter(filter);
      }
    } else {
      // For specific filters, apply sorting and filtering
      const currentSortedPredictions = [...currentPredictions].sort((a, b) => {
        // Always put Easter Bunny prediction (ID 331) at the end
        if (a.id === 331) return 1;
        if (b.id === 331) return -1;
        // Normal vote score sorting for all other predictions
        return (b.vote_score ?? 0) - (a.vote_score ?? 0);
      });
      const filteredPredictions = currentSortedPredictions.filter(p => {
        if (filter === 'pending') return !p.success_status || p.success_status === 'pending';
        if (filter === 'bet') return p.prediction_type === 'bet';
        if (filter === 'sentiment') return p.prediction_type === 'sentiment';
        if (filter === 'updated') return p.success_status === 'updated';
        return p.success_status === filter;
      });
      const filteredIds = filteredPredictions.map(p => p.id);
      if (assetType === 'btc') {
        setBtcActivePredictions(filteredIds);
        setBtcActiveFilter(filter);
      } else {
        setMstrActivePredictions(filteredIds);
        setMstrActiveFilter(filter);
      }
    }
  }, [predictions, mstrPredictions, btcActiveFilter, mstrActiveFilter]);

  // Reset chart function - clears all active predictions for the specified asset
  const resetChart = useCallback((assetType: 'btc' | 'mstr') => {
    if (assetType === 'btc') {
      setBtcActivePredictions([]);
    } else {
      setMstrActivePredictions([]);
    }
  }, []);

  // Filter chart data by time frame
  const filterDataByTimeFrame = useCallback((data: Array<{ time: string; value: number }>, timeFrame: string) => {
    if (timeFrame === 'ALL' || data.length === 0) return data;
    
    const now = new Date();
    let startDate: Date;
    
    switch (timeFrame) {
      case '30D':
        startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      case '3M':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '6M':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '1Y':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case '2Y':
        startDate = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
        break;
      case '3Y':
        startDate = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
        break;
      case '5Y':
        startDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
        break;
      default:
        return data;
    }
    
    return data.filter(point => {
      const pointDate = new Date(point.time);
      return pointDate >= startDate;
    });
  }, []);

  // Time frame handlers
  const handleBtcTimeFrameChange = useCallback((timeFrame: string) => {
    setBtcTimeFrame(timeFrame);
  }, []);

  const handleMstrTimeFrameChange = useCallback((timeFrame: string) => {
    setMstrTimeFrame(timeFrame);
  }, []);

  // Handle opening and closing the modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <GlobalStyles />
      <BrowserRouter>
        <ScrollProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<IntroPage isDarkMode={isDarkMode} theme={isDarkMode ? darkTheme : lightTheme} />} />
            <Route path="/btc" element={
              <MainLayout
                layout={layout}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                openModal={openModal}
                closeModal={closeModal}
                isModalOpen={isModalOpen}
                predictions={predictions}
                mstrPredictions={mstrPredictions}
                chartData={chartData}
                mstrChartData={mstrChartData}
                btcActivePredictions={btcActivePredictions}
                mstrActivePredictions={mstrActivePredictions}
                btcActiveFilter={btcActiveFilter}
                mstrActiveFilter={mstrActiveFilter}
                isLoading={isLoading}
                togglePrediction={togglePrediction}
                applyFilter={applyFilter}
                resetChart={resetChart}
                btcTimeFrame={btcTimeFrame}
                mstrTimeFrame={mstrTimeFrame}
                onBtcTimeFrameChange={handleBtcTimeFrameChange}
                onMstrTimeFrameChange={handleMstrTimeFrameChange}
                filterDataByTimeFrame={filterDataByTimeFrame}
                previewPrediction={previewPrediction}
                handlePreviewGenerated={handlePreviewGenerated}
                clearPreview={clearPreview}
                chartMode={chartMode}
                onChartModeChange={setChartMode}
                treasureHuntIndex={treasureHuntIndex}
                onTreasureHuntNavigate={setTreasureHuntIndex}
                isScreenshotMode={isScreenshotMode}
              />
            } />
            <Route path="/mstr" element={
              <MainLayout
                layout={layout}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                openModal={openModal}
                closeModal={closeModal}
                isModalOpen={isModalOpen}
                predictions={predictions}
                mstrPredictions={mstrPredictions}
                chartData={chartData}
                mstrChartData={mstrChartData}
                btcActivePredictions={btcActivePredictions}
                mstrActivePredictions={mstrActivePredictions}
                btcActiveFilter={btcActiveFilter}
                mstrActiveFilter={mstrActiveFilter}
                isLoading={isLoading}
                togglePrediction={togglePrediction}
                applyFilter={applyFilter}
                resetChart={resetChart}
                btcTimeFrame={btcTimeFrame}
                mstrTimeFrame={mstrTimeFrame}
                onBtcTimeFrameChange={handleBtcTimeFrameChange}
                onMstrTimeFrameChange={handleMstrTimeFrameChange}
                filterDataByTimeFrame={filterDataByTimeFrame}
                previewPrediction={previewPrediction}
                handlePreviewGenerated={handlePreviewGenerated}
                clearPreview={clearPreview}
                chartMode={chartMode}
                onChartModeChange={setChartMode}
                treasureHuntIndex={treasureHuntIndex}
                onTreasureHuntNavigate={setTreasureHuntIndex}
                isScreenshotMode={isScreenshotMode}
              />
            } />
            <Route path="/intro" element={<IntroPage isDarkMode={isDarkMode} theme={isDarkMode ? darkTheme : lightTheme} />} />
            <Route path="/leaderboard" element={<LeaderboardPage isDarkMode={isDarkMode} theme={isDarkMode ? darkTheme : lightTheme} />} />
            <Route path="/analytics" element={<UserAnalyticsDashboard />} />
            <Route path="/wrapped" element={<BitcoinWrapped />} />
            <Route path="/wrapped/:username" element={<BitcoinWrapped />} />
            <Route path="/generate-wrapped" element={<GenerateWrapped />} />
            <Route path="/wrapped/upgrade" element={<WrappedUpgrade />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/wrapped-simple" element={
              <div style={{ width: '100%', height: '100vh' }}>
                <iframe 
                  src="/wrapped-simple.html" 
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Bitcoin Wrapped Simple"
                />
              </div>
            } />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/mstr" element={<MSTRAdminDashboard />} />
            <Route path="/pretext" element={<PretextDemo />} />
            <Route path="/pinart" element={<MobilePinDemo />} />
            <Route path="/x9k7m2/haptic" element={<MobilePinDemo />} />
            <Route path="/w3gl9z/gpu" element={<WebGLPinDemo />} />
          </Routes>
        </ScrollProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;